import { FormControl, FormGroup, Grid, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import type { Dispatch, SetStateAction } from 'react';
import { useContext } from 'react';

import { Checkbox, FormControlLabel } from '@/contents/CampSite/styles';
import { ConstSearch } from '@/contents/constants';
import Button from '@/contents/General/Button';
import Icon from '@/contents/General/Icon';
import { Board, EditFooter } from '@/contents/General/Panels';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import { GlobalContext } from '@/contents/hooks/state';
import theme from '@/styles/theme';

const Container = styled(Box)`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  max-width: inherit;
  max-height: inherit;
  background: #f4f8f6;
  z-index: 12;
  overflow-y: auto;
`;

const Header = styled(Box)`
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  width: inherit;
  height: 64px;
  max-width: inherit;
  background: #e3e9e6;
  z-index: 12;
`;

type Props = {
  setShowSelectChoice: Dispatch<SetStateAction<boolean>>;
};

const SelectChoice = ({ setShowSelectChoice }: Props) => {
  const { checkedList, setCheckedList } = useContext(GlobalContext);

  const changeCheckedList = (value: string) => {
    if (checkedList.find((e) => e === value)) {
      setCheckedList(checkedList.filter((e) => e !== value));
    } else {
      setCheckedList([...checkedList, value]);
    }
  };

  const isChecked = (value: string) => {
    return checkedList.some((checked) => checked === value);
  };

  const close = () => {
    setShowSelectChoice(false);
  };

  const clear = () => {
    setCheckedList([]);
  };

  return (
    <Container>
      <Header>
        <IconButton
          onClick={close}
          sx={{ position: 'absolute', left: theme.spacing(3) }}
        >
          <Icon iconType="arrow-left" />
        </IconButton>
        <Typography fontSize={18} fontWeight={700}>
          こだわり条件
        </Typography>
      </Header>
      <Box marginTop={'64px'} marginBottom={'102px'} paddingBottom={6}>
        {ConstSearch.details.map((detail) => (
          <Box sx={{ paddingTop: theme.spacing(3) }} key={`${detail.name}`}>
            <Box
              sx={{
                padding: `0 ${theme.spacing(3)}`,
                paddingBottom: theme.spacing(2),
              }}
            >
              <Typography fontSize={16} fontWeight={700}>
                {detail.name}
              </Typography>
            </Box>
            {detail.children && (
              <Box
                sx={{
                  padding: `0 ${theme.spacing(3)}`,
                }}
              >
                <Board>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormGroup>
                      <Grid container spacing={2}>
                        {detail.children.map((item, index) => (
                          <Grid item xs={6} key={`${item.label + index}`}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  value={item.value}
                                  onChange={(e) =>
                                    changeCheckedList(e.target.value)
                                  }
                                  checked={isChecked(item.value)}
                                  icon={<Icon iconType="checkbox-off" />}
                                  checkedIcon={<Icon iconType="checkbox-on" />}
                                />
                              }
                              label={
                                <Typography
                                  fontSize={14}
                                  fontWeight={500}
                                  sx={{ lineHeight: 1.3 }}
                                >
                                  {item.label}
                                </Typography>
                              }
                              className={isChecked(item.value) ? 'checked' : ''}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>
                  </FormControl>
                </Board>
              </Box>
            )}
          </Box>
        ))}
      </Box>
      <EditFooter>
        <Stack space={3}>
          <Button
            onClick={clear}
            buttonSize="md"
            color="white"
            sx={{ width: '120px' }}
          >
            すべてクリア
          </Button>
          <Button
            onClick={close}
            buttonSize="md"
            color="primary"
            sx={{ width: '120px' }}
          >
            設定
          </Button>
        </Stack>
      </EditFooter>
    </Container>
  );
};

export default SelectChoice;
